import React from "react";
import type { UploadProps } from "./Upload";
import type { UploadFileWithPath } from "./Upload";
import { useUploadHandler } from "./useUploadHandler";

export function useUploadState(props: UploadProps) {
  // Normaliza accept para array e string
  const acceptArray = React.useMemo(
    () =>
      typeof props.accept === 'string'
        ? props.accept.split(',').map((a) => a.trim()).filter(Boolean)
        : props.accept ?? [],
    [props.accept]
  );
  const acceptStr = React.useMemo(
    () => (Array.isArray(props.accept) ? props.accept.join(',') : props.accept),
    [props.accept]
  );

  const [inputKey, setInputKey] = React.useState(0);
  const [internalSelectedFiles, setInternalSelectedFiles] = React.useState<UploadFileWithPath[]>([]);
  const [uploadProgress, setUploadProgress] = React.useState<number[]>([]);
  const [showSuccess, setShowSuccess] = React.useState(false);
  const [customError, setCustomError] = React.useState<string | null>(null);
  const [selectedIndexes, setSelectedIndexes] = React.useState<number[]>([]);
  const [draggedIdx, setDraggedIdx] = React.useState<number | null>(null);
  const [previewUrls, setPreviewUrls] = React.useState<(string | null)[]>([]);
  const [fileErrors, setFileErrors] = React.useState<(string | null)[]>([]);
  const [fileSuccess, setFileSuccess] = React.useState<(string | null)[]>([]);
  const [manualUploading, setManualUploading] = React.useState(false);
  const [abortControllers, setAbortControllers] = React.useState<AbortController[]>([]);
  const [directoryNotSupported, setDirectoryNotSupported] = React.useState(false);
  const [undoInfo, setUndoInfo] = React.useState<{
    files: UploadFileWithPath[];
    prevFiles: UploadFileWithPath[];
    visible: boolean;
  } | null>(null);

  React.useEffect(() => {
    if (props.directory) {
      const input = document.createElement('input');
      input.type = 'file';
      if (!('webkitdirectory' in input)) {
        setDirectoryNotSupported(true);
      } else {
        setDirectoryNotSupported(false);
      }
    } else {
      setDirectoryNotSupported(false);
    }
  }, [props.directory]);

  const isControlled = props.selectedFiles !== undefined;
  const selectedFiles: UploadFileWithPath[] = isControlled ? props.selectedFiles! : internalSelectedFiles;

  const {
    inputRef,
    dragActive,
    error,
    loading,
    handleFiles,
    setDragActive,
  } = useUploadHandler({
    accept: acceptArray,
    maxFiles: props.maxFiles,
    onFilesSelected: (files, error) => {
      const filesWithPath = Array.from(files).map((file: File) => ({
        file,
        relativePath: (file as File & { webkitRelativePath?: string; relativePath?: string }).webkitRelativePath ||
                      (file as File & { webkitRelativePath?: string; relativePath?: string }).relativePath ||
                      file.name,
      }));

      const duplicates = findDuplicateFiles(filesWithPath, selectedFiles);
      if (duplicates.length > 0) {
        setCustomError(
          duplicates.length === 1
            ? `Arquivo "${duplicates[0]}" já está na lista.`
            : `Os arquivos ${duplicates.map(n => `"${n}"`).join(", ")} já estão na lista.`
        );
        setShowSuccess(false);
        return;
      }

      if (!isControlled) setInternalSelectedFiles(filesWithPath);
      props.onFilesSelected(filesWithPath, error);
      setInputKey((k) => k + 1);
      setFileErrors(Array(filesWithPath.length).fill(null));
      setFileSuccess(Array(filesWithPath.length).fill(null));
      if (error) setCustomError(typeof error === "string" ? error : error?.message);
    },
    autoUpload: props.autoUpload,
    onUpload: props.onUpload
      ? (file, onProgress) => props.onUpload!(file, onProgress, undefined)
      : undefined,
    convertToWebP: props.convertToWebP,
    onProcessFile: props.onProcessFile,
    customValidation: props.customValidation,
    setUploadProgress,
    setShowSuccess,
    setCustomError,
  });

  function findDuplicateFiles(
    newFiles: UploadFileWithPath[],
    existingFiles: UploadFileWithPath[]
  ): string[] {
    const existing = new Set(
      existingFiles.map(f => `${f.file.name}|${f.file.size}`)
    );
    return newFiles
      .filter(f => existing.has(`${f.file.name}|${f.file.size}`))
      .map(f => f.file.name);
  }

  const [tooltipOpen, setTooltipOpen] = React.useState(false);
  const tooltipId = React.useId();

  const handleDrag = (e: React.DragEvent, active: boolean) => {
    e.preventDefault();
    e.stopPropagation();
    if (props.disabled) return;
    setDragActive(active);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (props.disabled) return;
    setDragActive(false);
    if (e.dataTransfer?.files && e.dataTransfer.files.length > 0) {
      handleFiles(e.dataTransfer.files);
    }
  };

  const resolveMessage = (
    msg: string | ((files: File[]) => string) | undefined,
    files: File[]
  ) => {
    if (typeof msg === "function") return msg(files);
    if (msg !== undefined) return msg;
    return undefined;
  };

  function mapFilesWithRelativePath(files: File[] | FileList): UploadFileWithPath[] {
    return Array.from(files).map((file: File) => ({
      file,
      relativePath: (file as File & { webkitRelativePath?: string; relativePath?: string }).webkitRelativePath ||
                    (file as File & { webkitRelativePath?: string; relativePath?: string }).relativePath ||
                    file.name,
    }));
  }

  const handleRemoveFile = (index: number) => {
    const removed = [selectedFiles[index]];
    const newFiles = selectedFiles.filter((_, i) => i !== index);
    if (!isControlled) setInternalSelectedFiles(newFiles);
    props.onFilesSelected(newFiles, undefined);
    setShowSuccess(false);
    setUndoInfo({
      files: removed,
      prevFiles: selectedFiles,
      visible: true,
    });
  };

  const toggleSelectFile = (idx: number) => {
    setSelectedIndexes((prev) =>
      prev.includes(idx) ? prev.filter((i) => i !== idx) : [...prev, idx]
    );
  };

  const selectAllFiles = () => {
    setSelectedIndexes(selectedFiles.map((_, i) => i));
  };

  const deselectAllFiles = () => {
    setSelectedIndexes([]);
  };

  const handleRemoveSelectedFiles = () => {
    const removed = selectedFiles.filter((_, i) => selectedIndexes.includes(i));
    const newFiles = selectedFiles.filter((_, i) => !selectedIndexes.includes(i));
    if (!isControlled) setInternalSelectedFiles(newFiles);
    props.onFilesSelected(newFiles, undefined);
    setSelectedIndexes([]);
    setShowSuccess(false);
    setUndoInfo({
      files: removed,
      prevFiles: selectedFiles,
      visible: true,
    });
  };

  const handleClearAllFiles = () => {
    if (!isControlled) setInternalSelectedFiles([]);
    props.onFilesSelected([], undefined);
    setSelectedIndexes([]);
    setShowSuccess(false);
    setCustomError(null);
  };

  const getDefaultErrorMessage =
    props.i18nMessages?.getDefaultErrorMessage || (() => "Arquivo inválido.");
  const getDefaultSuccessMessage =
    props.i18nMessages?.getDefaultSuccessMessage || (() => "Upload concluído!");

  const effectiveError =
    customError ||
    error ||
    resolveMessage(props.errorMessage, selectedFiles.map(f => f.file)) ||
    getDefaultErrorMessage(selectedFiles.map(f => f.file));

  const effectiveSuccess =
    resolveMessage(props.successMessage, selectedFiles.map(f => f.file)) ||
    getDefaultSuccessMessage(selectedFiles.map(f => f.file));

  const errorId = React.useId();
  const successId = React.useId();
  const helperId = React.useId();

  const handleDragStart = (idx: number) => setDraggedIdx(idx);

  const handleDragOver = (e: React.DragEvent, idx: number) => {
    e.preventDefault();
    if (draggedIdx === null || draggedIdx === idx) return;
    const files = [...selectedFiles];
    const [removed] = files.splice(draggedIdx, 1);
    files.splice(idx, 0, removed);
    if (!isControlled) setInternalSelectedFiles(files);
    props.onFilesSelected(files, undefined);
    setDraggedIdx(idx);
  };

  const handleDragEnd = () => setDraggedIdx(null);

  const handleFilesSelected = (files: FileList | File[]) => {
    const filesArr = Array.from(files as File[] | FileList);
    const filesWithPath = mapFilesWithRelativePath(filesArr);

    // Verifica duplicados
    const duplicates = findDuplicateFiles(filesWithPath, selectedFiles);
    if (duplicates.length > 0) {
      setCustomError(
        duplicates.length === 1
          ? `Arquivo "${duplicates[0]}" já está na lista.`
          : `Os arquivos ${duplicates.map(n => `"${n}"`).join(", ")} já estão na lista.`
      );
      setShowSuccess(false);
      return;
    }

    if (!isControlled) setInternalSelectedFiles(filesWithPath);
    props.onFilesSelected(filesWithPath, undefined);
    setInputKey((k) => k + 1);
    setFileErrors(Array(filesWithPath.length).fill(null));
    setFileSuccess(Array(filesWithPath.length).fill(null));
    setCustomError(null);
  };

  const handleButtonClick = (e?: React.MouseEvent<HTMLElement, MouseEvent>) => {
    if (props.containerProps?.buttonProps?.onClick) {
      props.containerProps.buttonProps.onClick(e);
    }
    inputRef.current?.click();
  };

  const previewUrlsRef = React.useRef<string[]>([]);
  const debounceTimeout = React.useRef<NodeJS.Timeout | null>(null);

  React.useEffect(() => {
    if (debounceTimeout.current) clearTimeout(debounceTimeout.current);

    debounceTimeout.current = setTimeout(() => {
      previewUrlsRef.current.forEach(url => {
        if (url) URL.revokeObjectURL(url);
      });

      const urls = selectedFiles.map(obj =>
        obj.file.type.startsWith('image/') ? URL.createObjectURL(obj.file) : null
      );
      setPreviewUrls(urls);
      previewUrlsRef.current = urls.filter((url): url is string => url !== null);
    }, 200);

    return () => {
      if (debounceTimeout.current) clearTimeout(debounceTimeout.current);
      previewUrlsRef.current.forEach(url => {
        if (url) URL.revokeObjectURL(url);
      });
      previewUrlsRef.current = [];
    };
  }, [selectedFiles]);

  const handleManualUpload = async () => {
    if (!props.onUpload) return;
    setManualUploading(true);
    setUploadProgress(Array(selectedFiles.length).fill(0));
    setFileErrors(Array(selectedFiles.length).fill(null));
    setFileSuccess(Array(selectedFiles.length).fill(null));
    const controllers = selectedFiles.map(() => new AbortController());
    setAbortControllers(controllers);

    try {
      await Promise.all(
        selectedFiles.map(async (fileObj, idx) => {
          try {
            let uploadUrl: string | undefined = undefined;
            let uploadHeaders: Record<string, string> | undefined = undefined;
            let uploadResponse: unknown = undefined;

            // PATCH: resolve endpoint dinamicamente
            if (props.uploadEndpointResolver) {
              const result = props.uploadEndpointResolver(fileObj.file);
              uploadUrl = result.url;
              uploadHeaders = result.headers;
            } else if (props.onPrepareUpload) {
              try {
                const result = await props.onPrepareUpload(fileObj.file);
                uploadUrl = result.url;
                uploadHeaders = result.headers;
              } catch (err) {
                setFileErrors((prev) => {
                  const next = [...prev];
                  next[idx] = "Erro ao preparar upload.";
                  return next;
                });
                return;
              }
            }

            if (props.onUpload) {
              uploadResponse = await props.onUpload(
                fileObj.file,
                (percent) => {
                  setUploadProgress((prev) => {
                    const next = [...prev];
                    next[idx] = percent;
                    return next;
                  });
                },
                controllers[idx].signal,
                uploadUrl,
                uploadHeaders
              );
            }

            setFileSuccess((prev) => {
              const next = [...prev];
              next[idx] = "Upload realizado com sucesso!";
              return next;
            });

            if (props.onUploadComplete) {
              props.onUploadComplete({
                file: fileObj.file,
                response: uploadResponse,
                index: idx,
              });
            }
          } catch (err) {
            setFileErrors((prev) => {
              const next = [...prev];
              next[idx] = controllers[idx].signal.aborted
                ? "Upload cancelado."
                : "Falha ao enviar arquivo.";
              return next;
            });
          }
        })
      );
      setShowSuccess(true);
      setCustomError(null);
    } catch (err) {
      setCustomError("Falha ao enviar arquivos.");
    } finally {
      setManualUploading(false);
      setAbortControllers([]);
    }
  };

  const totalProgress =
    selectedFiles.length > 0 && uploadProgress.length === selectedFiles.length
      ? Math.round(
          uploadProgress.reduce((a, b) => a + (b || 0), 0) / selectedFiles.length
        )
      : 0;

  const LARGE_FILE_LIMIT = 50 * 1024 * 1024;
  const hasLargeFiles = selectedFiles.some(f => f.file.size > LARGE_FILE_LIMIT);

  const handleUndoRemove = () => {
    if (undoInfo && undoInfo.prevFiles) {
      if (!isControlled) setInternalSelectedFiles(undoInfo.prevFiles);
      props.onFilesSelected(undoInfo.prevFiles, undefined);
      setShowSuccess(false);
      setSelectedIndexes([]);
    }
  };

  const handleRenameFile = (idx: number, newName: string) => {
    const files = [...selectedFiles];
    files[idx] = { ...files[idx], customName: newName };
    if (!isControlled) setInternalSelectedFiles(files);
    props.onFilesSelected(files, undefined);
  };

  const defaultMessages = {
    remove: "Remover",
    cancel: "Cancelar upload",
    upload: "Enviar",
    uploading: "Enviando...",
    selectAll: "Todos",
    deselectAll: "Nenhum",
    removeSelected: "Remover selecionados",
    clearAll: "Limpar todos",
    undo: "Desfazer",
    fileRemoved: "Arquivo removido – ",
    filesRemoved: "{count} arquivos removidos – ",
    directoryNotSupported: "Seu navegador não suporta seleção de pastas (webkitdirectory). Tente outro navegador.",
    largeFileWarning: "Atenção: Um ou mais arquivos excedem 50MB. O upload pode ser lento ou falhar dependendo do navegador e conexão.",
    defaultError: "Arquivo inválido.",
    defaultSuccess: "Upload concluído!",
  };

  const mergedMessages = { ...defaultMessages, ...(props.i18nMessages || {}) };

  React.useEffect(() => {
    if (props.onStatusChange) {
      if (customError) {
        props.onStatusChange({
          type: 'error',
          message: customError,
          files: selectedFiles,
        });
      } else if (showSuccess) {
        props.onStatusChange({
          type: 'success',
          message: effectiveSuccess,
          files: selectedFiles,
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customError, showSuccess]);

  // Para progresso global:
  React.useEffect(() => {
    if (props.onStatusChange && totalProgress > 0 && totalProgress < 100) {
      props.onStatusChange({
        type: 'progress',
        message: `Enviando arquivos... (${totalProgress}%)`,
        progress: totalProgress,
        files: selectedFiles,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [totalProgress]);

  return {
    // estados
    selectedFiles,
    selectedIndexes,
    uploadProgress,
    showSuccess,
    customError,
    draggedIdx,
    previewUrls,
    fileErrors,
    fileSuccess,
    manualUploading,
    abortControllers,
    directoryNotSupported,
    undoInfo,
    dragActive,
    loading,
    // handlers
    handleFilesSelected,
    handleFileSelection: handleFilesSelected, // compatibilidade legada
    handleRemoveFile,
    toggleSelectFile,
    selectAllFiles,
    deselectAllFiles,
    handleRemoveSelectedFiles,
    handleClearAllFiles,
    handleManualUpload,
    handleDragStart,
    handleDragOver,
    handleDragEnd,
    handleUndoRemove,
    setTooltipOpen,
    setUndoInfo,
    setInternalSelectedFiles,
    setCustomError,
    // refs e ids
    inputRef,
    tooltipOpen,
    tooltipId,
    errorId,
    successId,
    helperId,
    // mensagens
    effectiveError,
    effectiveSuccess,
    mergedMessages,
    // outros utilitários
    totalProgress,
    hasLargeFiles,
    handleButtonClick,
    handleDrag,
    handleDrop,
    acceptStr,
    inputKey,
    handleRenameFile,
  };
}